import { createElement, useCallback, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useAuth } from 'providers/AuthProvider';
import { startSandbox, buildSandboxUser } from 'services/api/sandbox';
import TurnstileWidget, { TurnstileWidgetHandle } from 'components/common/TurnstileWidget';

type SandboxLang = 'en' | 'el';

// Module-level in-flight guard. A component-scoped ref does NOT survive React's
// dev StrictMode remount (fresh ref) or any real remount, so the /try auto-start
// could fire twice and mint two sandbox instances — orphaning one and burning
// the per-IP rate limit. This module singleton dedupes concurrent/rapid start()
// calls across every component instance: while a start is in flight, further
// calls no-op. It clears when the attempt settles, so the retry button and a
// fresh /try visit still work.
let startInFlight = false;

/**
 * Starts a "Try the full plan" sandbox and drops the user into the demo
 * dashboard. Works for logged-in couples and anonymous visitors alike — the
 * demo session lives in per-tab sessionStorage, so a logged-in couple's real
 * session is untouched (they can Exit back to it).
 *
 * `start()` takes an optional language override — the public /try entry page
 * passes it to carry the visitor's landing-page language across; when absent it
 * derives the language from the active i18n locale. `error` lets a full-page
 * caller (the /try page) render a retry surface instead of only a snackbar.
 */
export function useStartSandbox() {
  const { enterSandbox } = useAuth();
  const { i18n, t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(false);

  // Invisible Cloudflare Turnstile — the BE `/api/sandbox/start` gate requires a
  // token when TURNSTILE_SECRET_KEY is set. Executed programmatically inside
  // start() so there's no up-front friction on the auto-starting /try page.
  // When the site key is unset (local dev), the slot renders nothing, execute()
  // is skipped, and the BE middleware is a matching no-op. Mirrors CreateEventForm.
  const turnstileSiteKey = (import.meta.env.VITE_TURNSTILE_SITE_KEY || '').trim();
  const turnstileRef = useRef<TurnstileWidgetHandle>(null);

  const start = useCallback(
    async (langOverride?: SandboxLang) => {
      if (starting || startInFlight) return;
      startInFlight = true;
      setStarting(true);
      setError(false);
      try {
        const lang =
          langOverride ||
          ((i18n.resolvedLanguage || i18n.language || 'en').startsWith('el') ? 'el' : 'en');
        let turnstileToken: string | undefined;
        if (turnstileSiteKey && turnstileRef.current) {
          turnstileToken = await turnstileRef.current.execute();
        }
        const resp = await startSandbox(lang, turnstileToken);
        // enterSandbox installs the demo session and hard-reloads into the
        // dashboard, so control doesn't return here on success. `starting` is
        // intentionally left true — the reload discards it, and resetting it
        // would only flash the idle state during the transition.
        enterSandbox(buildSandboxUser(resp), resp.token, resp.refreshToken);
      } catch {
        enqueueSnackbar(t('sandbox.start_error'), { variant: 'error' });
        setError(true);
        setStarting(false);
      } finally {
        startInFlight = false;
      }
    },
    [starting, i18n, enterSandbox, enqueueSnackbar, t, turnstileSiteKey],
  );

  // Invisible widget the caller must render (anywhere) so execute() can run.
  // Null when the site key is unconfigured — nothing to mount, token skipped.
  const turnstileSlot: ReactNode = turnstileSiteKey
    ? createElement(TurnstileWidget, {
        ref: turnstileRef,
        siteKey: turnstileSiteKey,
        mode: 'execute',
      })
    : null;

  return { start, starting, error, turnstileSlot };
}
